<template>
  <GcBaseLayout
    :title="'Bill of Materials'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="onBackToList"
    :formsConfig="formsConfig"
    class="bom-bulk-update-wizard"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Bill of Materials'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          draftState.transactionId ? 'Bulk Update' : 'New Bulk Update'
        "
        :secondaryLabel="draftState.transactionId || undefined"
        v-if="!loading"
      />
    </template>

    <div class="css-v2 bom-bulk-update-wizard-body" v-if="loaded">
      <!-- Sticky wizard header bar -->
      <div class="wizard-header-bar">
        <div class="wizard-header-center">
          <input
            v-model="draftState.description"
            :class="[
              'wizard-description-input',
              { 'has-error': saveAttempted && !draftState.description.trim() },
            ]"
            placeholder="Description"
            maxlength="200"
            :disabled="isReadOnly"
          />
          <span
            v-if="saveAttempted && !draftState.description.trim()"
            class="wizard-field-error"
          >
            Description is required
          </span>
        </div>
        <div class="wizard-header-right">
          <DxButton
            v-if="!isReadOnly && canEdit"
            :text="saving ? 'Saving...' : 'Save Draft'"
            type="default"
            styling-mode="contained"
            :disabled="saving || applyResult"
            @click="saveDraft"
          />
          <DxButton
            v-if="
              !isReadOnly && isDraft && canEdit && baseView.mode !== 'create'
            "
            text="Cancel transaction"
            type="danger"
            styling-mode="outlined"
            class="wizard-cancel-txn-btn"
            :disabled="applyResult"
            @click="cancelTransaction"
          />
          <DxButton
            :text="'Close'"
            type="normal"
            styling-mode="outlined"
            class="wizard-close-btn"
            @click="onBackToList(false)"
          />
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="wizard-loading-panel">
        <span>Loading transaction...</span>
      </div>

      <!-- Audit record view — review mode (Phase 7) -->
      <div v-else-if="isReadOnly" class="audit-record">
        <GcBomBulkUpdateAuditRecord
          :transaction-id="draftState.transactionId"
          :description="draftState.description"
          :status="draftState.status"
          :date-created="transactionMeta.DateCreated"
          :created-by="transactionMeta.CreatedBy"
          :date-committed="transactionMeta.DateCommitted"
          :committed-by="transactionMeta.CommittedBy"
          :boms-updated-count="transactionMeta.BomsUpdatedCount"
          :boms-included-count="transactionMeta.BomsIncludedCount"
          :search-criteria="draftState.searchCriteria"
          :material-update-config="draftState.materialUpdateConfig"
          :process-update-config="draftState.processUpdateConfig"
          :diff-snapshot="draftState.diffSnapshot"
          :bom-outcomes="bomOutcomes"
          :activity-log="activityLog"
        />
      </div>

      <!-- Wizard content — create / edit mode -->
      <template v-else>
        <!-- Step progress indicator -->
        <div class="wizard-steps-bar">
          <div
            v-for="(label, i) in stepLabels"
            :key="i"
            class="wizard-step-group"
          >
            <div
              :class="[
                'wizard-step',
                { 'is-active': currentStep === i + 1 },
                {
                  'is-done':
                    currentStep > i + 1 ||
                    (i + 1 === 2 && currentStep === 1 && canNavigateToStep2) ||
                    (i + 1 === 3 && currentStep === 2 && canNavigateToStep3),
                },
                { 'is-disabled': applyResult },
              ]"
              @click="!applyResult && onStepClick(i + 1)"
            >
              <span class="step-num">{{ i + 1 }}</span>
              {{ label }}
            </div>
            <span v-if="i < stepLabels.length - 1" class="step-div">›</span>
          </div>
        </div>

        <!-- Draft activity log — collapsible (Obs 3) -->
        <!-- <div
          v-if="draftState.transactionId && activityLog.length"
          class="wizard-activity-log"
        >
          <DxButton
            :text="'Activity Log ' + (showActivityLog ? '\u25b2' : '\u25bc')"
            type="normal"
            styling-mode="text"
            class="activity-log-toggle"
            @click="showActivityLog = !showActivityLog"
          />
          gc-bom-bulk-update-activity-log rendered here in Phase 7
        </div> -->

        <!-- Step content body -->
        <div class="wizard-step-body">
          <!-- Step 1: Search Criteria — v-show keeps state when navigating back -->
          <GcBomBulkUpdateStepSearch
            v-show="currentStep === 1"
            :isReadOnly="isReadOnly"
            :savedCriteria="draftState.searchCriteria"
            @search-complete="onSearchComplete"
            @criteria-change="onSearchCriteriaChange"
            :baseView="baseView"
            :showLoading="showLoading"
            @go-next="goNext"
          />

          <!-- Step 2: Configure Operations — two-column layout: configure + active filters sidebar -->
          <div v-show="currentStep === 2" class="step-2-layout">
            <div class="step-2-main">
              <GcBomBulkUpdateStepConfigure
                ref="configureStep"
                :matched-bom-list="draftState.matchedBomList"
                :excluded-bom-ids="draftState.excludedBomIds"
                :material-update-config="draftState.materialUpdateConfig"
                :process-update-config="draftState.processUpdateConfig"
                :has-material-criteria="hasMaterialCriteria"
                :has-process-criteria="hasProcessCriteria"
                :is-read-only="isReadOnly"
                @config-update="onConfigureUpdate"
                @validation-change="onConfigureValidationChange"
              />
            </div>
            <GcBomBulkUpdateStepSearch
              :show-sidebar-only="true"
              :is-read-only="true"
              :saved-criteria="draftState.searchCriteria"
              :base-view="baseView"
              :show-loading="showLoading"
              v-if="currentStep === 2"
            />
          </div>

          <!-- Step 3: Preview & Diff -->
          <div v-show="currentStep === 3">
            <GcBomBulkUpdateStaleDiffBanner
              v-if="staleDiffWarningActive && draftState.diffComputedAt"
              :stale-boms="staleBoms"
              :deleted-boms="deletedBoms"
              :diff-computed-at="draftState.diffComputedAt"
              :is-read-only="isReadOnly"
              @acknowledge="onStaleDiffAcknowledge"
              @recompute="onStaleDiffRecompute"
            />
            <GcBomBulkUpdateStepPreview
              ref="previewStep"
              :active="currentStep === 3"
              :matched-bom-list="draftState.matchedBomList"
              :excluded-bom-ids="draftState.excludedBomIds"
              :material-update-config="draftState.materialUpdateConfig"
              :process-update-config="draftState.processUpdateConfig"
              :diff-snapshot="draftState.diffSnapshot"
              :is-read-only="isReadOnly"
              :search-criteria="draftState.searchCriteria"
              @diff-computed="onDiffComputed"
              @exclude-bom="onExcludeBomFromPreview"
              @include-bom="onIncludeBomFromPreview"
              @auto-exclude-zero-affected="onAutoExcludeZeroAffected"
            />
          </div>

          <!-- Step 4: Apply -->
          <div v-show="currentStep === 4">
            <GcBomBulkUpdateStepApply
              ref="applyStep"
              :active="currentStep === 4"
              :boms-to-update="includedBomCount"
              :boms-excluded="draftState.excludedBomIds.length"
              :lines-changed="computedLinesChanged"
              :lines-added="computedLinesAdded"
              :stale-diff-warning-active="staleDiffWarningActive"
              :stale-boms="staleBoms"
              :deleted-boms="deletedBoms"
              :diff-computed-at="draftState.diffComputedAt"
              :is-read-only="isReadOnly"
              :apply-result="applyResult"
              :matched-bom-list="draftState.matchedBomList"
              :excluded-bom-ids="draftState.excludedBomIds"
              @stale-diff-acknowledge="onStaleDiffAcknowledge"
              @stale-diff-recompute="onStaleDiffRecompute"
              @go-back="goBack"
              @apply="onApplyStep"
              @back-to-list="onBackToList"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Footer navigation in GcBaseLayout #footer slot — renders outside the scroll view, always at bottom -->
    <template #footer>
      <div
        v-if="loaded && !isReadOnly && (currentStep === 2 || currentStep === 3)"
        class="wizard-footer-bar"
      >
        <DxButton
          :text="currentStep === 3 ? 'Back to Configure' : 'Back'"
          type="normal"
          styling-mode="outlined"
          :disabled="applyResult"
          @click="goBack"
        />
        <DxButton
          :text="currentStep === 3 ? 'Proceed to Apply' : 'Preview Changes'"
          type="default"
          styling-mode="contained"
          :disabled="!canAdvance || applyResult"
          class="wizard-next-btn"
          @click="goNext"
        />
      </div>
    </template>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";

import { FeatureStorage } from "@/_services/storage";
import { BomBulkUpdateService } from "@/_services/api/production/bom-bulk-update.service";
import { DefaultCompanyBranchDivisionService } from "@/_services/api/sales/default-company-branch-dicision.service";
import DxButton from "devextreme-vue/button";
import { IsResponseDataValid } from "../../../_helpers/ui/helpers";
import GcBomBulkUpdateStepSearch from "@/components/production/bom-bulk-update/gc-bom-bulk-update-step-search";
import GcBomBulkUpdateStepConfigure from "@/components/production/bom-bulk-update/gc-bom-bulk-update-step-configure";
import GcBomBulkUpdateStepPreview from "@/components/production/bom-bulk-update/gc-bom-bulk-update-step-preview";
import GcBomBulkUpdateStaleDiffBanner from "@/components/production/bom-bulk-update/gc-bom-bulk-update-stale-diff-banner";
import GcBomBulkUpdateStepApply from "@/components/production/bom-bulk-update/gc-bom-bulk-update-step-apply";
import GcBomBulkUpdateAuditRecord from "@/components/production/bom-bulk-update/gc-bom-bulk-update-audit-record";
import { mapGetters } from "vuex";

export default {
  props: {},
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    DxButton,
    GcBomBulkUpdateStepSearch,
    GcBomBulkUpdateStepConfigure,
    GcBomBulkUpdateStepPreview,
    GcBomBulkUpdateStaleDiffBanner,
    GcBomBulkUpdateStepApply,
    GcBomBulkUpdateAuditRecord,
  },
  data() {
    return {
      loading: false,
      saving: false,
      saveAttempted: false,
      configureAttempted: false,
      configureStepValid: false,
      configChangedSincePreview: false,
      searchResultsStale: false,
      isReadOnly: false,
      companyCode: null,
      currentStep: 1,
      showActivityLog: false,
      activityLog: [],
      staleBoms: [],
      deletedBoms: [],
      staleDiffWarningActive: false,
      staleDiffChecking: false,
      recomputePending: false,
      stepLabels: ["Search", "Configure", "Preview Changes", "Apply"],
      draftState: {
        transactionId: null,
        description: "",
        status: "D",
        searchCriteria: {
          materialCriteria: [],
          processCriteria: [],
          scopeFilters: {},
        },
        matchedBomList: [],
        selectedBomIds: [],
        excludedBomIds: [],
        materialUpdateConfig: null,
        processUpdateConfig: null,
        diffSnapshot: null,
        diffComputedAt: null,
        staleDiffAcknowledged: false,
        staleDiffAcknowledgementReason: "",
      },
      loaded: false,
      applyResult: null,
      transactionMeta: {
        DateCreated: null,
        CreatedBy: null,
        DateCommitted: null,
        CommittedBy: null,
        BomsUpdatedCount: 0,
        BomsIncludedCount: 0,
      },
      bomOutcomes: [],
      lastSavedDraftState: null,
    };
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    canNavigateToStep2() {
      return (
        this.draftState.matchedBomList.length > 0 && !this.searchResultsStale
      );
    },
    canNavigateToStep3() {
      return (
        !!this.draftState.diffComputedAt && !this.configChangedSincePreview
      );
    },
    isDraft() {
      return this.draftState.status === "D";
    },
    canAdvance() {
      if (this.currentStep === 1) {
        return this.draftState.matchedBomList.length > 0;
      }
      if (this.currentStep === 2) {
        return this.includedBomCount > 0 && this.configureStepValid;
      }
      if (this.currentStep === 3) {
        return (
          !this.staleDiffChecking &&
          !this.staleDiffWarningActive &&
          this.includedBomCount > 0
        );
      }
      return false;
    },
    includedBomCount() {
      return this.draftState.matchedBomList.filter(
        (b) => !this.draftState.excludedBomIds.includes(b.BomCode),
      ).length;
    },
    hasMaterialCriteria() {
      const sc = this.draftState.searchCriteria;
      return (sc.materialCriteria || sc.MaterialCriteria || []).length > 0;
    },
    hasProcessCriteria() {
      const sc = this.draftState.searchCriteria;
      return (sc.processCriteria || sc.ProcessCriteria || []).length > 0;
    },
    hasBothCriteria() {
      return this.hasMaterialCriteria && this.hasProcessCriteria;
    },
    canEdit() {
      return FeatureStorage.checkPermission("BulkBOMUpdate", "create");
    },
    computedLinesChanged() {
      if (
        !this.draftState.diffSnapshot ||
        !this.draftState.diffSnapshot.BomDiffs
      )
        return 0;
      var count = 0;
      var bomDiffs = this.draftState.diffSnapshot.BomDiffs;
      var excludedSet = new Set(this.draftState.excludedBomIds);
      Object.entries(bomDiffs).forEach(([bomCode, bom]) => {
        if (excludedSet.has(bomCode)) return;
        var materials = bom.MaterialLines || [];
        var processes = bom.ProcessLines || [];
        materials.concat(processes).forEach((line) => {
          if (["QtyChanged", "RunRateChanged"].includes(line.ChangeType))
            count++;
        });
      });
      return count;
    },
    computedLinesAdded() {
      if (
        !this.draftState.diffSnapshot ||
        !this.draftState.diffSnapshot.BomDiffs
      )
        return 0;
      var count = 0;
      var bomDiffs = this.draftState.diffSnapshot.BomDiffs;
      var excludedSet = new Set(this.draftState.excludedBomIds);
      Object.entries(bomDiffs).forEach(([bomCode, bom]) => {
        if (excludedSet.has(bomCode)) return;
        var materials = bom.MaterialLines || [];
        var processes = bom.ProcessLines || [];
        materials.concat(processes).forEach((line) => {
          if (["Added", "Replaced"].includes(line.ChangeType)) count++;
        });
      });
      return count;
    },
    hasDraftChanges() {
      if (!this.lastSavedDraftState) return true;
      return (
        JSON.stringify(this.draftState) !==
        JSON.stringify(this.lastSavedDraftState)
      );
    },
  },
  async created() {
    this.showLoading(true);
    const mode = this.baseView.mode;
    const id = this.baseView.id;

    DefaultCompanyBranchDivisionService.getDefault().then((res) => {
      this.companyCode = res.data.value[0].CompanyCode;
    });

    if (mode === "create") {
      this.currentStep = 1;
      this.showLoading(false);
    } else if (mode === "edit") {
      await this.loadDraft(id);
      if (
        (this.currentStep === 3 || this.currentStep === 4) &&
        this.draftState.diffComputedAt
      ) {
        this.showLoading(true);
        this.checkStaleDiff();
      } else {
        this.showLoading(false);
      }
    } else if (mode === "review") {
      this.isReadOnly = true;
      await this.loadAuditRecord(id);
      this.showLoading(false);
    } else {
      this.showLoading(false);
    }

    this.$nextTick(() => {
      this.loaded = true;
    });
  },
  methods: {
    // ─── Navigation ────────────────────────────────────────────────────────────

    async goNext() {
      if (!this.canAdvance) return;
      if (this.currentStep === 2) {
        this.configureAttempted = true;
        const valid =
          this.$refs.configureStep && this.$refs.configureStep.validate();
        if (!valid) {
          this.configureStepValid = false;
          return;
        }
        // Always force a fresh diff when advancing to Preview
        this.draftState.diffSnapshot = null;
        this.draftState.diffComputedAt = null;
        if (this.$refs.previewStep) this.$refs.previewStep.reset();
      }
      if (this.currentStep === 3) {
        // Persist snapshot and DiffComputedAt before entering Apply
        const saved = await this.saveDraft();
        if (!saved) return;
      }
      if (this.currentStep < 4) {
        this.currentStep++;
      }
      // TC-096: auto-check stale diff whenever Apply step becomes active
      if (this.currentStep === 4) {
        this.checkStaleDiff();
      }
    },
    goBack() {
      if (this.currentStep > 1) this.currentStep--;
    },
    onStepClick(step) {
      if (step < this.currentStep) {
        this.currentStep = step;
      } else if (
        step === 2 &&
        this.currentStep === 1 &&
        this.canNavigateToStep2
      ) {
        this.currentStep = 2;
      } else if (
        step === 3 &&
        this.currentStep === 2 &&
        this.canNavigateToStep3
      ) {
        this.currentStep = 3;
        this.checkStaleDiff();
      }
    },
    onBackToList(ignore = false) {
      // Skip confirmation if: explicitly ignored, read-only mode, after successful apply, or no changes made
      if (
        ignore ||
        this.isReadOnly ||
        this.applyResult ||
        !this.hasDraftChanges
      ) {
        this.setIgnoreRouteChangeConfirmation();
      }
      this.$router.push({ name: "bill-of-materials-bulk-update" });
    },

    // ─── State handlers ────────────────────────────────────────────────────────

    onStepUpdate(patch) {
      this.draftState = Object.assign({}, this.draftState, patch);
    },
    onSearchComplete(payload) {
      this.draftState.matchedBomList = payload.matchedBomList;
      this.draftState.searchCriteria = payload.searchCriteria;
      this.draftState.selectedBomIds = payload.matchedBomList.map(
        (b) => b.BomCode,
      );
      this.draftState.excludedBomIds = [];
      this.draftState.materialUpdateConfig = null;
      this.draftState.processUpdateConfig = null;
      this.configureAttempted = false;
      this.configureStepValid = false;
      this.searchResultsStale = false;
      this.currentStep = 2;
    },
    onSearchCriteriaChange({ isStale }) {
      this.searchResultsStale = isStale;
    },
    onConfigureValidationChange({ isValid }) {
      this.configureStepValid = isValid;
    },
    onConfigureUpdate(payload) {
      this.draftState.excludedBomIds = payload.excludedBomIds;
      this.draftState.materialUpdateConfig = payload.materialUpdateConfig;
      this.draftState.processUpdateConfig = payload.processUpdateConfig;
      if (this.draftState.diffComputedAt) {
        this.configChangedSincePreview = true;
      }
    },
    async onDiffComputed({ diffSnapshot, diffComputedAt }) {
      this.draftState.diffSnapshot = diffSnapshot;
      this.draftState.diffComputedAt = diffComputedAt;
      this.configChangedSincePreview = false;
      // Fresh compute clears any previous stale state, then immediately re-check
      this.staleBoms = [];
      this.deletedBoms = [];
      this.staleDiffWarningActive = false;
      if (this.recomputePending) {
        this.recomputePending = false;
        this.activityLog.push({
          Action: "DiffRecomputed",
          Timestamp: diffComputedAt || new Date().toISOString(),
        });
        await this.saveDraft(); // silent save to persist new snapshot and DiffComputedAt
      }
      this.$nextTick(() => {
        this.checkStaleDiff();
      });
    },
    onExcludeBomFromPreview(bomCode) {
      if (!this.draftState.excludedBomIds.includes(bomCode)) {
        this.draftState.excludedBomIds = [
          ...this.draftState.excludedBomIds,
          bomCode,
        ];
      }
    },
    onIncludeBomFromPreview(bomCode) {
      this.draftState.excludedBomIds = this.draftState.excludedBomIds.filter(
        (id) => id !== bomCode,
      );
    },

    onAutoExcludeZeroAffected(bomCodes) {
      this.draftState.excludedBomIds = [
        ...new Set([...this.draftState.excludedBomIds, ...bomCodes]),
      ];
    },

    // ─── Stale diff (UI-07.5) ──────────────────────────────────────────────────

    buildComputeDiffPayload() {
      var payload = {
        BomCodes: this.draftState.selectedBomIds,
        MaterialUpdateConfig: this.draftState.materialUpdateConfig || null,
        ProcessUpdateConfig: this.draftState.processUpdateConfig || null,
      };

      // MaterialSearchCriteria — UpdateQtyAll only
      var matConfig = this.draftState.materialUpdateConfig;
      var matCriteria = this.draftState.searchCriteria;

      if (
        matConfig &&
        matConfig.Operation === "UpdateQtyAll" &&
        matCriteria &&
        matCriteria.MaterialCriteria &&
        matCriteria.MaterialCriteria.length
      ) {
        payload.MaterialSearchCriteria = matCriteria.MaterialCriteria;
      }

      // ProcessSearchCriteria — UpdateRunRateAll only
      var procConfig = this.draftState.processUpdateConfig;
      var procCriteria = this.draftState.searchCriteria;

      if (
        procConfig &&
        procConfig.Operation === "UpdateRunRateAll" &&
        procCriteria &&
        procCriteria.ProcessCriteria &&
        procCriteria.ProcessCriteria.length
      ) {
        payload.ProcessSearchCriteria = procCriteria.ProcessCriteria;
      }

      // Include TransactionId if draft has been saved (logs DiffRecomputed)
      if (this.draftState.transactionId) {
        payload.TransactionId = this.draftState.transactionId;
      }

      return payload;
    },

    async checkStaleDiff() {
      if (this.draftState.staleDiffAcknowledged) return;
      if (
        !this.draftState.diffComputedAt ||
        !this.draftState.selectedBomIds.length
      )
        return;
      this.staleDiffChecking = true;
      try {
        var payload = {
          BomCodes: this.draftState.selectedBomIds,
          DiffComputedAt: this.draftState.diffComputedAt,
          MaterialUpdateConfig: this.draftState.materialUpdateConfig || null,
          ProcessUpdateConfig: this.draftState.processUpdateConfig || null,
          DiffSnapshot: this.buildDiffSnapshot(),
        };

        // MaterialSearchCriteria — UpdateQtyAll only
        var matConfig = this.draftState.materialUpdateConfig;
        var matCriteria = this.draftState.searchCriteria;

        if (
          matConfig &&
          matConfig.Operation === "UpdateQtyAll" &&
          matCriteria &&
          matCriteria.MaterialCriteria &&
          matCriteria.MaterialCriteria.length
        ) {
          payload.MaterialSearchCriteria = matCriteria.MaterialCriteria;
        }

        // ProcessSearchCriteria — UpdateRunRateAll only
        var procConfig = this.draftState.processUpdateConfig;
        var procCriteria = this.draftState.searchCriteria;

        if (
          procConfig &&
          procConfig.Operation === "UpdateRunRateAll" &&
          procCriteria &&
          procCriteria.ProcessCriteria &&
          procCriteria.ProcessCriteria.length
        ) {
          payload.ProcessSearchCriteria = procCriteria.ProcessCriteria;
        }

        var res = await BomBulkUpdateService.checkStaleDiff(payload);
        this.staleBoms = res.data.StaleBoms || [];
        this.deletedBoms = res.data.DeletedBoms || [];

        // Auto-exclude deleted BOMs so they can't be applied
        if (this.deletedBoms.length) {
          const deletedCodes = this.deletedBoms.map((b) =>
            typeof b === "string" ? b : b.BomCode,
          );
          const newExclusions = deletedCodes.filter(
            (code) => !this.draftState.excludedBomIds.includes(code),
          );
          if (newExclusions.length) {
            this.draftState.excludedBomIds = [
              ...this.draftState.excludedBomIds,
              ...newExclusions,
            ];
          }
        }

        this.staleDiffWarningActive =
          this.staleBoms.length > 0 || this.deletedBoms.length > 0;
        this.draftState.staleDiffAcknowledged = false;
      } catch (e) {
        this.showLoading(false);
        // eslint-disable-next-line no-console
        console.warn("Stale diff check failed", e);
      } finally {
        this.showLoading(false);
        this.staleDiffChecking = false;
      }
    },
    buildDiffSnapshot() {
      const snapshot = this.draftState.diffSnapshot;
      if (!snapshot || !snapshot.BomDiffs) return { BomDiffs: {} };
      const stripped = {};
      Object.entries(snapshot.BomDiffs).forEach(([bomCode, bomDiff]) => {
        stripped[bomCode] = {
          MaterialLines: (bomDiff.MaterialLines || []).map((l) => ({
            ProcessCode: l.ProcessCode,
            Code: l.Code,
            CurrentValue: l.CurrentValue,
            NewValue: l.NewValue,
            ChangeType: l.ChangeType,
          })),
          ProcessLines: (bomDiff.ProcessLines || []).map((l) => ({
            ProcessCode: l.ProcessCode,
            Code: l.Code,
            CurrentValue: l.CurrentValue,
            NewValue: l.NewValue,
            ChangeType: l.ChangeType,
          })),
        };
      });
      return { BomDiffs: stripped };
    },

    // ─── Stale diff acknowledge / recompute ────────────────────────────────────

    async onStaleDiffAcknowledge(reason) {
      this.draftState.staleDiffAcknowledged = true;
      this.draftState.staleDiffAcknowledgementReason = reason;
      this.staleDiffWarningActive = false;
      await this.saveDraft(); // silent save to persist acknowledgment in draft
      this.activityLog.push({
        Action: "StaleDiffAcknowledged",
        Timestamp: new Date().toISOString(),
        Notes: reason,
      });
    },
    async onStaleDiffRecompute() {
      await this.saveDraft(); // silent save to persist new snapshot and DiffComputedAt
      this.staleDiffWarningActive = false;
      this.staleBoms = [];
      this.deletedBoms = [];
      this.draftState.diffSnapshot = null;
      this.draftState.diffComputedAt = null;
      this.draftState.staleDiffAcknowledged = false;
      this.recomputePending = true;
      if (this.$refs.previewStep) {
        this.$refs.previewStep.reset();
        this.$nextTick(async () => {
          await this.$refs.previewStep.initDiff();
        });
      }
    },

    // ─── Apply (US-08) ────────────────────────────────────────────────────────

    async onApplyStep({ noteText }) {
      // noteText is sent by Apply step but not yet used by API
      void noteText;
      this.showLoading(true);
      try {
        const res = await BomBulkUpdateService.apply(
          this.draftState.transactionId,
        );
        this.applyResult = res.data;
      } catch (error) {
        this.displayServerError(
          error,
          "Failed to apply bulk update. Please try again.",
        );
      } finally {
        this.showLoading(false);
      }
    },

    // ─── Draft persistence (UI-03.3) ───────────────────────────────────────────

    async saveDraft() {
      if (this.isReadOnly) return;
      this.saveAttempted = true;
      if (!this.draftState.description.trim()) return;
      this.showLoading(true);
      try {
        this.saving = true;
        const payload = this.buildDraftPayload();
        const res = await BomBulkUpdateService.saveDraft(payload);
        this.draftState.transactionId = res.data.TransactionId;

        if (this.baseView.mode === "create") {
          this.setIgnoreRouteChangeConfirmation();
          this.$router.replace({
            name: "bill-of-materials-bulk-update-edit",
            params: { id: res.data.TransactionId },
          });
        }

        if (res.data.ActivityLog) {
          this.activityLog = res.data.ActivityLog;
        }

        // Update baseline after successful save
        this.lastSavedDraftState = JSON.parse(JSON.stringify(this.draftState));

        this.$notify("Draft saved \u2014 " + res.data.TransactionId, "success");
        return true;
      } catch (err) {
        this.showLoading(false);
        this.displayServerError(err, "Failed to save draft. Please try again.");
      } finally {
        this.showLoading(false);
        this.saving = false;
      }
    },
    buildDraftPayload() {
      return {
        TransactionId: this.draftState.transactionId,
        CompanyCode: this.companyCode,
        Description: this.draftState.description,
        CurrentStep: this.currentStep,
        SearchCriteriaJson: JSON.stringify(this.draftState.searchCriteria),
        MatchedBomListJson: JSON.stringify(this.draftState.matchedBomList),
        SelectedBomIdsJson: JSON.stringify(this.draftState.selectedBomIds),
        ExcludedBomIdsJson: JSON.stringify(this.draftState.excludedBomIds),
        MaterialUpdateConfigJson: JSON.stringify(
          this.draftState.materialUpdateConfig,
        ),
        ProcessUpdateConfigJson: JSON.stringify(
          this.draftState.processUpdateConfig,
        ),
        DiffSnapshotJson: JSON.stringify(this.draftState.diffSnapshot),
        DiffComputedAt: this.draftState.diffComputedAt,
        StaleDiffAcknowledged: this.draftState.staleDiffAcknowledged,
        StaleDiffAcknowledgedNote:
          this.draftState.staleDiffAcknowledgementReason,
      };
    },

    // ─── Draft resume (UI-03.4) ────────────────────────────────────────────────

    async loadDraft(transactionId) {
      try {
        this.loading = true;
        const res = await BomBulkUpdateService.getTransaction(transactionId);
        if (IsResponseDataValid(res)) {
          const t = res.data.value[0];

          // Restore company code from the draft so subsequent saves target the same company
          if (t.CompanyCode) {
            this.companyCode = t.CompanyCode;
          }

          this.draftState = {
            transactionId: t.TransactionId,
            description: t.Description || "",
            status: t.Status.trim(),
            searchCriteria: this.parseJson(t.SearchCriteriaJson, {
              materialCriteria: [],
              processCriteria: [],
              scopeFilters: {},
            }),
            matchedBomList: this.parseJson(t.MatchedBomListJson, []),
            selectedBomIds: this.parseJson(t.SelectedBomIdsJson, []),
            excludedBomIds: this.parseJson(t.ExcludedBomIdsJson, []),
            materialUpdateConfig: this.parseJson(
              t.MaterialUpdateConfigJson,
              null,
            ),
            processUpdateConfig: this.parseJson(
              t.ProcessUpdateConfigJson,
              null,
            ),
            diffSnapshot: this.parseJson(t.DiffSnapshotJson, null),
            diffComputedAt: t.DiffComputedAt || null,
            staleDiffAcknowledged: false,
            staleDiffAcknowledgementReason: "",
          };

          this.currentStep = t.CurrentStep || 1;

          if (t.ActivityLog) {
            this.activityLog = t.ActivityLog;
          }
          // Set baseline for change detection (must be after all state is restored)
          this.$nextTick(() => {
            this.lastSavedDraftState = JSON.parse(
              JSON.stringify(this.draftState),
            );
          });
        } else {
          this.$notify(
            "Transaction not found or could not be loaded.",
            "error",
          );
          this.$router.push({ name: "bill-of-materials-bulk-update" });
        }
      } catch (err) {
        this.displayServerError(
          err,
          "Failed to load transaction. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },
    parseJson(str, fallback) {
      if (!str) return fallback;
      try {
        return JSON.parse(str);
      } catch (e) {
        return fallback;
      }
    },

    // ─── Load Audit Record (Phase 7 — review mode) ─────────────────────────────
    async loadAuditRecord(transactionId) {
      try {
        this.loading = true;
        const res = await BomBulkUpdateService.getTransactionRecord(
          transactionId,
        );

        if (res && res.data) {
          const t = res.data;

          this.transactionMeta = {
            DateCreated: t.DateCreated || null,
            CreatedBy: t.CreatedBy || null,
            DateCommitted: t.DateCommitted || null,
            CommittedBy: t.CommittedBy || null,
            BomsUpdatedCount: t.BomsUpdatedCount || 0,
            BomsIncludedCount: t.BomsIncludedCount || 0,
          };

          this.draftState = {
            transactionId: t.TransactionId,
            description: t.Description || "",
            status: t.Status.trim(),
            searchCriteria: t.SearchCriteria || {
              materialCriteria: [],
              processCriteria: [],
              scopeFilters: {},
            },
            matchedBomList: t.MatchedBomList || [],
            selectedBomIds: t.SelectedBomIds || [],
            excludedBomIds: t.ExcludedBomIds || [],
            materialUpdateConfig: t.MaterialUpdateConfig || null,
            processUpdateConfig: t.ProcessUpdateConfig || null,
            diffSnapshot: t.DiffSnapshot || null,
            diffComputedAt: t.DiffComputedAt || null,
            staleDiffAcknowledged: false,
            staleDiffAcknowledgementReason: "",
          };

          this.bomOutcomes = t.BomOutcomes || [];
          if (t.ActivityLog) {
            this.activityLog = t.ActivityLog;
          }
        } else {
          this.$notify(
            "Transaction not found or could not be loaded.",
            "error",
          );
          this.$router.push({ name: "bill-of-materials-bulk-update" });
        }
      } catch (err) {
        this.displayServerError(
          err,
          "Failed to load transaction. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },

    // ─── Cancel flow (UI-03.5) ─────────────────────────────────────────────────

    async cancelTransaction() {
      if (this.isReadOnly || !this.isDraft) return;
      const confirmed = await this.$confirm(
        "Are you sure you want to cancel this transaction? This action cannot be undone.",
        "Cancel Transaction",
      );
      if (!confirmed) return;

      this.showLoading(true);
      try {
        await BomBulkUpdateService.cancel(this.draftState.transactionId);
        this.$notify("Transaction cancelled.", "success");
        this.setIgnoreRouteChangeConfirmation();
        this.$router.push({ name: "bill-of-materials-bulk-update" });
      } catch (err) {
        this.showLoading(false);
        this.displayServerError(err, "Cancel failed. Please try again.");
      } finally {
        this.showLoading(false);
      }
    },
  },
  watch: {
    getHotKeySave(newVal) {
      if (newVal) {
        this.$store.dispatch("hotKey/setHotKeySave", false);
        this.saveDraft();
      }
    },
  },
};
</script>

<style lang="scss">
.bom-bulk-update-wizard {
  .bom-bulk-update-wizard-body {
    padding: 12px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  // ── Wizard header bar ──────────────────────────────────────────────────────
  .wizard-header-bar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 16px;
    background: #fff;
    border-bottom: 1px solid #dee2e6;
    position: sticky;
    top: 0;
    z-index: 10;

    .wizard-header-center {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .wizard-header-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .wizard-description-input {
      width: 100%;
      padding: 4px 10px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      font-size: 0.9em;

      &.has-error {
        border-color: #dc3545;
      }

      &:disabled {
        background: #f8f9fa;
        color: #6c757d;
      }
    }

    .wizard-field-error {
      font-size: 0.75em;
      color: #dc3545;
      margin-top: 2px;
    }

    .wizard-close-btn {
      white-space: nowrap;
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  .wizard-loading-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #6c757d;
    font-size: 0.95em;
  }

  // ── Step progress indicator ────────────────────────────────────────────────
  .wizard-steps-bar {
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: 1px solid #dee2e6;
    background: #fff;

    .wizard-step-group {
      display: flex;
      align-items: center;
    }

    .wizard-step {
      display: flex;
      align-items: center;
      height: 48px;
      padding: 0 12px;
      gap: 8px;
      font-size: 12px;
      font-weight: 500;
      color: #adb5bd;
      border-bottom: 2px solid transparent;
      white-space: nowrap;
      cursor: default;

      .step-num {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 1.5px solid currentColor;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        flex-shrink: 0;
      }

      &.is-active {
        color: #03a9f4;
        border-bottom-color: #03a9f4;
      }

      &.is-done {
        color: #17b26a;
        cursor: pointer;

        .step-num {
          background: #17b26a;
          border-color: #17b26a;
          color: #fff;
        }

        &:hover {
          opacity: 0.8;
        }

        &.is-disabled {
          color: #adb5bd;
          cursor: default;
          opacity: 0.6;

          .step-num {
            background: transparent;
            border-color: currentColor;
            color: #adb5bd;
          }

          &:hover {
            opacity: 0.6;
          }
        }
      }

      &.is-disabled {
        color: #adb5bd;
        cursor: default;
        opacity: 0.6;

        &:hover {
          opacity: 0.6;
        }
      }
    }

    .step-div {
      color: #dee2e6;
      font-size: 16px;
      padding: 0 2px;
    }
  }

  // ── Activity log toggle ────────────────────────────────────────────────────
  .wizard-activity-log {
    padding: 4px 16px;
    border-bottom: 1px solid #dee2e6;

    .activity-log-toggle {
      font-size: 0.85em;
      padding: 4px 0;
    }
  }

  // ── Step body ──────────────────────────────────────────────────────────────
  .wizard-step-body {
    flex: 1;
    padding-top: 10px;
    min-height: 0;

    .step-placeholder {
      min-height: 200px;
    }
  }

  // ── Step 2 layout — BOM grid + filters sidebar ────────────────────────────
  .step-2-layout {
    display: flex;
    gap: 10px;
    align-items: flex-start;

    .step-2-main {
      flex: 1;
      min-width: 0;
    }
  }

  // ── Footer navigation ──────────────────────────────────────────────────────
  .wizard-footer-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #fff;
    width: 100%;

    .wizard-next-btn {
      margin-left: auto;
    }
  }
}
</style>
